'use client';
import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,  } from 'recharts';

// Backend integration: GET /api/reports/utilization?range=7d
const dailyData = [
  { date: 'Mar 22', vehicles: 71, equipment: 58, people: 83 },
  { date: 'Mar 23', vehicles: 68, equipment: 55, people: 79 },
  { date: 'Mar 24', vehicles: 74, equipment: 62, people: 88 },
  { date: 'Mar 25', vehicles: 80, equipment: 67, people: 91 },
  { date: 'Mar 26', vehicles: 76, equipment: 60, people: 85 },
  { date: 'Mar 27', vehicles: 82, equipment: 70, people: 92 },
  { date: 'Mar 28', vehicles: 78, equipment: 65, people: 87 },
];

const assetBreakdown = [
  { name: 'Unit-14', utilization: 94 },
  { name: 'Unit-08', utilization: 88 },
  { name: 'Unit-05', utilization: 82 },
  { name: 'Unit-19', utilization: 79 },
  { name: 'Unit-17', utilization: 61 },
  { name: 'Unit-03', utilization: 55 },
  { name: 'Unit-22', utilization: 38 },
  { name: 'Unit-11', utilization: 12 },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 shadow-xl">
      <p className="text-xs font-semibold text-zinc-400 mb-1.5">{label}</p>
      {payload.map((p: any) => (
        <p key={`tt-${p.dataKey}`} className="text-xs" style={{ color: p.color }}>{p.name}: {p.value}%</p>
      ))}
    </div>
  );
};

export default function UtilizationReport({ dateRange }: { dateRange: string }) {
  const avgVehicles = Math.round(dailyData.reduce((s, d) => s + d.vehicles, 0) / dailyData.length);
  const avgEquipment = Math.round(dailyData.reduce((s, d) => s + d.equipment, 0) / dailyData.length);
  const avgPeople = Math.round(dailyData.reduce((s, d) => s + d.people, 0) / dailyData.length);

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Avg Vehicle Utilization', value: `${avgVehicles}%`, color: 'text-amber-400', trend: '+4% vs prior period' },
          { label: 'Avg Equipment Utilization', value: `${avgEquipment}%`, color: 'text-sky-400', trend: '+2% vs prior period' },
          { label: 'Avg Personnel Utilization', value: `${avgPeople}%`, color: 'text-violet-400', trend: '+6% vs prior period' },
        ].map((s) => (
          <div key={`util-sum-${s.label}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">{s.label}</p>
            <p className={`text-3xl font-bold tabular-nums ${s.color}`}>{s.value}</p>
            <p className="text-xs text-emerald-400 mt-1">{s.trend}</p>
          </div>
        ))}
      </div>

      {/* Trend chart */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-zinc-200 mb-4">Utilization by Asset Type — {dateRange}</h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={dailyData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="vehGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#38BDF8" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="ppGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#A78BFA" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(39 39 42)" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'rgb(113 113 122)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'rgb(113 113 122)' }} axisLine={false} tickLine={false} domain={[0, 100]} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="vehicles" name="Vehicles" stroke="#F59E0B" strokeWidth={2} fill="url(#vehGrad)" dot={false} />
            <Area type="monotone" dataKey="equipment" name="Equipment" stroke="#38BDF8" strokeWidth={2} fill="url(#eqGrad)" dot={false} />
            <Area type="monotone" dataKey="people" name="Personnel" stroke="#A78BFA" strokeWidth={2} fill="url(#ppGrad)" dot={false} />
            <Legend wrapperStyle={{ fontSize: '11px', color: 'rgb(113 113 122)' }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Per-asset breakdown */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-zinc-200 mb-4">Vehicle Utilization by Unit</h3>
        <div className="space-y-2.5">
          {assetBreakdown.map((a) => (
            <div key={`asset-util-${a.name}`} className="flex items-center gap-3">
              <span className="font-mono text-xs text-zinc-400 w-14 flex-shrink-0">{a.name}</span>
              <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${a.utilization}%`,
                    backgroundColor: a.utilization >= 80 ? '#34D399' : a.utilization >= 60 ? '#F59E0B' : '#F87171',
                  }}
                />
              </div>
              <span className={`text-xs font-mono font-semibold w-10 text-right tabular-nums ${a.utilization >= 80 ? 'text-emerald-400' : a.utilization >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                {a.utilization}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}